import type { Issue } from '../types';
import { formatP } from '../stats/pvalue';

export interface IssueText {
	title: string;
	detail: string;
	suggestion?: string;
}

/**
 * Motor çıktılarının İngilizce karşılıkları.
 *
 * Türkçe metin motorların içinde üretilir ve `tr` için olduğu gibi gösterilir;
 * bu modül yalnızca `en` için şablon sağlar. Böylece motor mantığı (regex,
 * eşik, skor) değişmeden arayüz iki dilli olur.
 */

const p = (v: unknown): string =>
	typeof v === 'number' && Number.isFinite(v) ? formatP(v) : 'p = —';

export function issueTextEn(issue: Issue): IssueText | null {
	const m = (issue.meta ?? {}) as Record<string, unknown>;
	const raw = typeof m.raw === 'string' ? m.raw : undefined;
	const claim = typeof m.claim === 'string' ? m.claim : undefined;

	switch (issue.code) {
		case 'stat.p_threshold_flip':
			return {
				title: 'Reported p contradicts the statistic',
				detail:
					`${raw ? `${raw}. ` : ''}The p-value computed from this statistic is ${p(m.recomputedP)}; ` +
					`the reported value is ${p(m.reportedP)}. The two fall on opposite sides of the significance ` +
					`threshold (α = ${m.alpha ?? 0.05}). This may be a typo or the wrong test statistic; verify it against the raw data.`,
				suggestion:
					'Recheck the test statistic and degrees of freedom; recompute the p-value in your statistical software.'
			};

		case 'stat.p_mismatch':
			return {
				title: 'Reported p does not exactly match the statistic',
				detail:
					`${raw ? `${raw}. ` : ''}The reported value is ${p(m.reportedP)}; the value computed from the ` +
					`statistic is ${p(m.recomputedP)}. The difference does not change the significance decision, but it may be a rounding or typing error.`,
				suggestion: 'Recompute the p-value and align it with the reported value.'
			};

		case 'stat.p_missing':
			return {
				title: 'Statistic reported without a p-value',
				detail:
					`${raw ? `${raw}. ` : ''}APA 7 expects a p-value alongside the test statistic.` +
					(m.suggestedP !== undefined ? ` The computed value would be ${p(m.suggestedP)}.` : ''),
				suggestion: 'Add the p-value to the expression.'
			};

		case 'stat.df_missing':
			return m.kind === 'F'
				? {
						title: 'Degrees of freedom missing for F',
						detail: `${raw ? `${raw}. ` : ''}APA style expects F(df1, df2) = …`,
						suggestion: 'Report both degrees of freedom next to F.'
					}
				: {
						title: 'Degrees of freedom not reported',
						detail: `${raw ? `${raw}. ` : ''}APA style expects the degrees of freedom with the statistic (e.g. t(48) = 2.41).`,
						suggestion: 'Add the degrees of freedom in parentheses.'
					};

		case 'stat.decimal_comma':
			return {
				title: 'Decimal comma used',
				detail: `${raw ? `${raw}. ` : ''}This is acceptable in Turkish text, but some journals require a decimal point.`,
				suggestion: 'Convert to a decimal point if the journal style requires it.'
			};

		case 'stat.impossible_value':
			return {
				title: 'Impossible statistic value',
				detail: `${raw ? `${raw}. ` : ''}The value lies outside its possible range; this is usually an extraction or typing error.`,
				suggestion: 'Check the statistic against the analysis output.'
			};

		case 'stat.operator_corrupt': {
			const text = typeof m.text === 'string' ? m.text : issue.quote;
			const recovered =
				typeof m.recoveredOp === 'string' && typeof m.pValue === 'number'
					? ` The intended expression may be p ${m.recoveredOp} ${formatP(m.pValue).replace('p = ', '').replace('p < ', '')}.`
					: '';
			return {
				title: 'Possible operator loss',
				detail:
					`"${text ?? '—'}" has a comma or semicolon where a comparison operator belongs. ` +
					`This is a known PDF extraction failure.${recovered} The expression was not used for any verdict.`,
				suggestion: 'Inspect the sentence in the PDF and correct the operator manually.'
			};
		}

		case 'stat.significance_claim':
			return {
				title: 'Significance claim without numerical support',
				detail: `"${claim ?? issue.quote ?? '—'}" appears without a reported p-value in the same sentence.`,
				suggestion: 'Report the test statistic and p-value in the same sentence.'
			};

		case 'cite.missing_in_references': {
			const candidates = Array.isArray(m.candidates) && m.candidates.length
				? ` Similar entries: ${(m.candidates as string[]).join(', ')}.`
				: '';
			return {
				title: 'In-text citation missing from the reference list',
				detail: `"${m.citation ?? issue.quote ?? '—'}" has no matching entry in the reference list.${candidates}`,
				suggestion: 'Add the missing entry, or check the year and surname spelling.'
			};
		}

		case 'cite.year_mismatch':
			return {
				title: 'Citation year does not match the reference list',
				detail: `"${m.citation ?? issue.quote ?? '—'}" cites ${m.year ?? 'a year'}, but the matching reference lists ${m.matchedYear ?? 'a different year'}.`,
				suggestion: 'Align the citation year with the publication year.'
			};

		case 'cite.author_mismatch':
			return {
				title: 'Author name does not match the reference list',
				detail: `"${m.citation ?? issue.quote ?? '—'}" uses a surname that appears as "${m.authors ?? '—'}" in the references.`,
				suggestion: 'Check the surname spelling, including Turkish characters.'
			};

		case 'cite.orphan_reference':
			return {
				title: 'Reference never cited in the text',
				detail: `"${m.raw ?? issue.quote ?? '—'}" appears in the reference list but is never cited in the text.`,
				suggestion: 'Cite the source where relevant, or remove it from the reference list.'
			};

		case 'cite.no_reference_section':
			return {
				title: 'Reference section not found',
				detail: `${m.count ?? 'Several'} in-text citations were found, but no "References" / "Bibliography" section was detected.`,
				suggestion: 'Make sure the reference heading is explicit (References, Bibliography or Kaynakça).'
			};

		case 'struct.missing_section':
			return {
				title: `Missing section: ${sectionName(m.sectionKey)}`,
				detail: `The "${sectionName(m.sectionKey)}" section could not be detected. The IMRaD structure expects it.`,
				suggestion: `Add an explicit "${sectionName(m.sectionKey)}" heading.`
			};

		case 'struct.section_order':
			return {
				title: 'Section order differs from the expected structure',
				detail: 'The results section appears before the method section.',
				suggestion: 'IMRaD order: Introduction → Method → Results → Discussion.'
			};

		case 'struct.table_unreferenced':
			return {
				title: 'Table never referenced in the text',
				detail: `A caption for Table ${m.number ?? '?'} exists, but the text never refers to "Table ${m.number ?? '?'}".`,
				suggestion: `Refer to "Table ${m.number ?? '?'}" at the relevant point in the text.`
			};

		case 'struct.figure_unreferenced':
			return {
				title: 'Figure never referenced in the text',
				detail: `A caption for Figure ${m.number ?? '?'} exists, but the text never refers to "Figure ${m.number ?? '?'}".`,
				suggestion: `Refer to "Figure ${m.number ?? '?'}" at the relevant point in the text.`
			};

		case 'struct.ref_without_caption':
			return {
				title: 'Referenced in text but no caption found',
				detail: `"${m.raw ?? issue.quote ?? '—'}" appears in the text, but no matching ${m.refType === 'figure' ? 'figure' : 'table'} caption was found.`,
				suggestion: 'Add a numbered caption to the table or figure.'
			};

		case 'struct.figure_numbering':
			return {
				title: 'Gap in table/figure numbering',
				detail: `The following numbers appear to be missing: ${m.gaps ?? '—'}.`,
				suggestion: 'Renumber tables and figures consecutively.'
			};

		case 'struct.keywords_missing':
			return {
				title: 'No keywords line found',
				detail: 'An abstract is present, but no "Keywords" line could be detected.',
				suggestion: 'Add 3–6 keywords below the abstract.'
			};

		case 'lang.causal_overclaim':
			return {
				title: 'Causal language conflicts with the study design',
				detail:
					`"${m.match ?? '—'}" asserts causation, but the study was identified as ` +
					`${designName(m.design)}; this design does not support a causal inference.`,
				suggestion: 'Reframe causally: “was associated with”, “co-occurred with”, “predicted”.'
			};

		case 'lang.causal_no_design':
			return {
				title: 'Causal language, study design unclear',
				detail: `"${m.match ?? '—'}" asserts causation. The method section did not reveal a study design; if it is not experimental, this is an overclaim.`,
				suggestion: 'State the design explicitly, or soften the causal claim.'
			};

		case 'lang.absolutist':
			return {
				title: 'Overclaim',
				detail: `"${m.match ?? '—'}" presents the finding more broadly than the evidence supports.`,
				suggestion: 'Hedge the claim: “the findings suggest”, “is consistent with”.'
			};

		case 'lang.generalization':
			return {
				title: 'Generalization',
				detail: `"${m.match ?? '—'}" generalizes beyond the sample.`,
				suggestion: 'State the sample boundaries (“in this sample”, “in this group”).'
			};

		case 'lang.stat_claim_without_test':
			return {
				title: 'Significance claim without a test',
				detail: `"${m.match ?? '—'}" appears without a test statistic or p-value in the same sentence.`,
				suggestion: 'Report the statistic and p-value in the same sentence.'
			};

		case 'lang.passive_overuse':
			return {
				title: 'High passive-voice density',
				detail: `About ${Math.round(Number(m.ratio ?? 0) * 100)}% of sentences use the passive voice. The active voice clarifies agency.`,
				suggestion: 'Rewrite key findings in the active voice.'
			};

		case 'prov.low_text_quality':
			return {
				title: 'Text extraction is not reliable',
				detail:
					`Text quality score ${Math.round(Number(m.score ?? 0) * 100)}/100. ` +
					`${String(m.notes ?? '')} Findings in this document require manual verification.`,
				suggestion: 'Upload a PDF with a proper text layer, or run OCR first.'
			};

		case 'prov.scanned_pdf':
			return {
				title: `${m.emptyPages ?? 'Some'} pages have no text layer`,
				detail: 'Scanned pages cannot be analysed. Try an OCR-processed version.',
				suggestion: 'Run OCR and re-upload.'
			};

		case 'prov.missing_glyphs':
			return {
				title: 'Unrecognized glyphs',
				detail: `About ${(Number(m.ratio ?? 0) * 100).toFixed(1)}% of the text contains unreadable characters (font or encoding problem).`,
				suggestion: 'Export the PDF with embedded fonts.'
			};

		case 'prov.short_document':
			return {
				title: 'Document is very short',
				detail: 'There is not enough text for a meaningful analysis; the results are not representative.',
				suggestion: 'Upload the full manuscript.'
			};

		default:
			return null;
	}
}

function sectionName(key: unknown): string {
	const names: Record<string, string> = {
		abstract: 'Abstract',
		keywords: 'Keywords',
		introduction: 'Introduction',
		method: 'Method',
		results: 'Results',
		discussion: 'Discussion',
		references: 'References',
		appendix: 'Appendix'
	};
	return typeof key === 'string' ? (names[key] ?? key) : 'section';
}

function designName(key: unknown): string {
	const names: Record<string, string> = {
		experimental: 'experimental',
		quasi: 'quasi-experimental',
		correlational: 'correlational/cross-sectional',
		case: 'a case study',
		unknown: 'unclear'
	};
	return typeof key === 'string' ? (names[key] ?? key) : 'unclear';
}
